<template>
  <component :is="as" :class="[formStyles.inputWrap, { [styles.autofilled]: isAutofilled }]">
    <typography
      as="label"
      :for="inputId"
      v-if="title"
      :variant="isLabelOnTop ? inputVariants.label : inputVariants.title"
      :class="[styles.formLabel, { [styles.labelOnTop]: isLabelOnTop }]"
    >
      {{ title }}
    </typography>
    <input
      ref="inputRef"
      v-bind="$attrs"
      v-model="inputValue"
      :type="type || 'text'"
      :id="inputId"
      :name="name"
      :class="formStyles.input"
      :inputmode="inputType.inputMode"
      :disabled="disabled"
      @focusin="onFocusInput"
      @focusout="focusInput(false)"
      @input="onInput"
      @change="syncInputFromDom"
      @animationstart="onAnimationStart"
      v-maska="maskaOptions"
      @maska="onMaska"
      :maxlength="maxLength"
      :autocomplete="autocomplete"
    />

    <TooltipCustom
      :to-right="tooltipToRight"
      v-if="tooltipIcon"
      :text="tooltipText"
      :class="styles.tooltipContainer"
    >
      <InfoIcon :class="styles.tooltipIcon" />
    </TooltipCustom>

    <BaseButton
      type="secondary"
      size="xs"
      v-if="inputType.type === 'password' && eyeIconShown"
      :class="[styles.close, { [styles.empty]: !value.length }, styles.secondary, styles.iconOnly]"
      @click="changeIsShowPass()"
    >
      <template #left-icon>
        <EyeIcon v-if="!isShowPass" />
        <EyeSlashIcon v-else-if="isShowPass" />
      </template>
    </BaseButton>
    <BaseButton
      type="secondary"
      size="xs"
      v-else-if="clearable && value"
      :class="[styles.close, { [styles.empty]: !value.length }]"
      @click="clearFunc()"
    >
      <template #left-icon>
        <CloseIcon />
      </template>
    </BaseButton>
    <span v-else-if="$slots['right-icon']" :class="[styles.close, styles.empty]">
      <slot name="right-icon" />
    </span>

    <typography
      as="span"
      :variant="inputVariants.error"
      :class="styles.error"
      v-if="error && errorMessage"
    >
      {{ errorMessage }}
    </typography>
  </component>
</template>

<script setup lang="ts">
 
import { Mask, type MaskaDetail, type MaskInputOptions } from 'maska';
import { vMaska } from 'maska/vue';
import { useId } from 'radix-vue';
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';

import { thousandsMaskFromValue } from 'src/logic/functions/make-mask.function';
import CloseIcon from 'shared/icons/close.svg';
import { BaseButton, Typography, type TypographyVariant } from 'src/shared/ui';
import { useConvert } from 'src/shared/utils/useConvert';

import EyeSlashIcon from './icons/eye-slash.svg';
import EyeIcon from './icons/eye.svg';
import InfoIcon from '../../icons/help-circle-icon.svg';

import styles from './styles.module.css';
import TooltipCustom from 'src/shared/ui/tooltip/tooltip-custom.vue';

defineOptions({
  inheritAttrs: false,
});

defineSlots<{
  default(props: object): never;
  'right-icon'(props: object): never;
}>();

export type BaseInputEmits = {
  'update:value': [string];
  'update:unmasked': [string];
  'update:error': [boolean];
};
const emit = defineEmits<BaseInputEmits>();

export interface BaseInputProps {
  /** html tag for inputWrap  */
  as?: string;
  value?: string;
  unmasked?: string;
  title: string;
  /** isRequired icon for label */
  clearable?: boolean;
  //** callback func for clear button */
  clearCallback?: () => void;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  tooltipIcon?: boolean;
  tooltipText?: string;
  tooltipToRight?: boolean;
  /**
   * variant for input default: 'text'
   */
  variant?: 'phone' | 'password' | 'text' | 'numeric' | 'email' | string;
  valueModifiers?: { default: () => Record<string, never> };
  size?: 's' | 'm' | 'l';
  mask?: string | string[];
  thousandsFormat?: boolean;
  decimalFormat?: boolean;
  maxLength?: number;
  initializeUnmasked?: boolean;
  eyeIconShown?: boolean;
  convertable?: boolean;
  autocomplete?: string;
  name?: string;
}

const props = withDefaults(defineProps<BaseInputProps>(), {
  as: 'div',
  value: '',
  unmasked: '',
  title: '',
  clearable: false,
  tooltipToRight: false,
  clearCallback: () => {},
  error: false,
  errorMessage: '',
  disabled: false,
  initializeUnmasked: false,
  variant: '',
  size: 'l',
  mask: '',
  thousandsFormat: false,
  decimalFormat: false,
  eyeIconShown: true,
  convertable: false,
  autocomplete: '',
  name: undefined,
});

const inputRef = ref<HTMLInputElement>();
const isFocus = ref(false);
const isAutofilled = ref(false);
const inputId = useId();
const inputValue = ref(props.value);
const maskaObject = reactive({ masked: '', unmasked: '', completed: false } as MaskaDetail);
let autofillPollInterval: ReturnType<typeof setInterval> | undefined;

const maskaOptions = computed<MaskInputOptions>(() => {
  if (props.decimalFormat) {
    return {
      number: { locale: 'ru', fraction: 2, unsigned: true },
      postProcess: (value) => value.replace(/\u00a0/g, ' '),
    };
  }

  if (props.thousandsFormat) {
    return {
      mask: thousandsMaskFromValue,
      eager: true,
    };
  }

  if (!props.mask || (Array.isArray(props.mask) && props.mask.length === 0)) {
    return {};
  }

  return {
    mask: props.mask,
    eager: true,
  };
});

const onMaska = (event: CustomEvent<MaskaDetail>): void => {
  maskaObject.masked = event.detail.masked;
  maskaObject.unmasked = event.detail.unmasked;
  maskaObject.completed = event.detail.completed;
};

const { convertToPercentage } = useConvert();

const isLabelOnTop = computed<boolean>((): boolean => {
  return !!inputValue.value.length || isFocus.value || isAutofilled.value;
});

const sizeToInputVariants: Record<
  Required<BaseInputProps>['size'],
  { error: TypographyVariant; title: TypographyVariant; label: TypographyVariant }
> = {
  s: { error: 'caption-2', label: 'caption-1', title: 'caption-2' },
  m: { error: 'caption-2', label: 'caption-2', title: 'body-1' },
  l: { error: 'caption-2', label: 'caption-2', title: 'body-3' },
};
const inputVariants = computed(() => {
  return sizeToInputVariants[props.size];
});

const formStyles = computed<{ input: string[]; inputWrap: string[] }>(
  (): { input: string[]; inputWrap: string[] } => {
    const classes = {
      input: [styles.formInput, styles[props.size]],
      inputWrap: [styles.form, styles[props.size]],
    };
    if (props.disabled) {
      classes.inputWrap.push(styles.disabled);
      classes.input.push(styles.disabled);
    }
    if (inputValue.value.length) {
      classes.input.push(styles.filled);
    }
    if (isLabelOnTop.value && props.title) {
      classes.input.push(styles.inputDown);
    }
    if (props.tooltipIcon) {
      classes.input.push(styles.tooltipPadding);
    }
    if (props.error) {
      classes.inputWrap.push(styles.invalid);
      classes.input.push(styles.invalid);
    }
    if (isFocus.value && !props.error) {
      classes.inputWrap.push(styles.focus);
    }
    return classes;
  },
);

export type inputModeType =
  | 'text'
  | 'none'
  | 'tel'
  | 'url'
  | 'email'
  | 'numeric'
  | 'decimal'
  | 'search'
  | undefined;

const inputType = computed((): { inputMode: inputModeType; type: string } => {
  if (props.decimalFormat) {
    return { inputMode: 'decimal', type: 'text' };
  }
  if (props.variant === 'phone') {
    return {
      inputMode: 'tel',
      type: 'text',
    };
  } else if (props.variant === 'password') {
    return {
      inputMode: 'text',
      type: 'password',
    };
  } else if (props.variant === 'email') {
    return {
      inputMode: 'email',
      type: 'text',
    };
  } else if (props.variant === 'numeric') {
    return {
      inputMode: 'numeric',
      type: 'number',
    };
  }
  return {
    inputMode: 'text',
    type: 'text',
  };
});

const type = ref(inputType.value.type);

const isShowPass = computed<boolean>((): boolean => {
  return type.value === 'text';
});

const onFocusInput = (): void => {
  if (props.variant === 'phone' && !inputValue.value.startsWith('+')) {
    inputValue.value = `+${inputValue.value}`;
  }

  focusInput(true);
  requestAnimationFrame(syncInputFromDom);
};

const focusInput = (value: boolean): void => {
  isFocus.value = value;

  if (!value) {
    syncInputFromDom();

    if (props.convertable) {
      inputValue.value = convertToPercentage(props.value);
      inputFunc();
    }
  }
};

const updateAutofillState = (): void => {
  const el = inputRef.value;
  if (!el) return;

  try {
    isAutofilled.value = el.matches(':-webkit-autofill');
  } catch {
    isAutofilled.value = false;
  }
};

const syncInputFromDom = (): void => {
  const el = inputRef.value;
  if (!el) return;

  updateAutofillState();

  if (el.value !== inputValue.value) {
    inputValue.value = el.value;
    inputFunc();
  }
};

const onAnimationStart = (event: AnimationEvent): void => {
  if (event.animationName.includes('onAutoFillStart')) {
    syncInputFromDom();
  }
};

const onInput = (): void => {
  updateAutofillState();
  inputFunc();
};

const formattedPropValue = computed(() => {
  if (!props.thousandsFormat && !props.decimalFormat) return props.value;
  const options = maskaOptions.value;
  const masked = new Mask(options).masked(props.value);
  return options.postProcess ? options.postProcess(masked) : masked;
});

const inputFunc = (): void => {
  // Maska also fires input when formatting a value supplied by the parent.
  // Do not feed that display-only update back into linked field calculations.
  if (
    (!props.thousandsFormat && !props.decimalFormat) ||
    inputValue.value !== formattedPropValue.value
  ) {
    emit('update:value', inputValue.value);
  }
  emit('update:unmasked', maskaObject.unmasked);
};

const startAutofillPolling = (): void => {
  stopAutofillPolling();
  autofillPollInterval = setInterval(syncInputFromDom, 200);
};

const stopAutofillPolling = (): void => {
  if (autofillPollInterval === undefined) return;
  clearInterval(autofillPollInterval);
  autofillPollInterval = undefined;
};

const clearFunc = (): void => {
  props.clearCallback();
  inputValue.value = '';
  inputFunc();
};

const changeIsShowPass = (): void => {
  type.value = isShowPass.value ? 'password' : 'text';
};

watch(
  () => props.value,
  (newValue: string) => {
    inputValue.value = newValue;
  },
);

const autofillSyncTimeouts: ReturnType<typeof setTimeout>[] = [];

onMounted(() => {
  [0, 100, 300, 600, 1200, 2000].forEach((delay) => {
    autofillSyncTimeouts.push(setTimeout(syncInputFromDom, delay));
  });
  startAutofillPolling();
  autofillSyncTimeouts.push(setTimeout(stopAutofillPolling, 8000));
});

onUnmounted(() => {
  autofillSyncTimeouts.forEach((id) => clearTimeout(id));
  stopAutofillPolling();
});

watch(
  () => props.initializeUnmasked,
  (newValue: boolean) => {
    if (!newValue) return;
    inputValue.value = props.unmasked;
  },
  { immediate: true },
);

const focus = (): void => {
  inputRef.value?.focus();
};

defineExpose({
  focus,
  syncFromDom: syncInputFromDom,
});
</script>
